import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    TextField,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    useMediaQuery,
    useTheme,
    Divider,
    Stack,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

// ─── shared helpers ───────────────────────────────────────────────────────────

const formatBetrag = (value, typ) =>
    `${parseFloat(value || 0).toFixed(typ === 'KISTEN' ? 0 : 2)} ${typ === 'KISTEN' ? 'Kisten' : '€'}`;

const BetragChip = ({ value, typ }) => (
    <Chip
        label={formatBetrag(value, typ)}
        size="small"
        color="primary"
        sx={{ borderRadius: '6px', fontWeight: 800, fontSize: '0.78rem' }}
    />
);

// ─── desktop columns ──────────────────────────────────────────────────────────

const columns = [
    {
        field: 'grund',
        headerName: 'Strafgrund',
        flex: 2,
        minWidth: 160,
        renderCell: ({ value }) => (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {value || 'Unbenannter Strafgrund'}
            </Typography>
        ),
    },
    {
        field: 'individueller_text',
        headerName: 'Beschreibung',
        flex: 3,
        minWidth: 180,
        renderCell: ({ value }) => (
            <Typography variant="body2" color="text.secondary">
                {value || 'Standard-Strafe'}
            </Typography>
        ),
    },
    {
        field: 'betrag',
        headerName: 'Betrag',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ value, row }) => <BetragChip value={value} typ={row.typ} />,
    },
];

// ─── mobile card ──────────────────────────────────────────────────────────────

const MobileCard = ({ row }) => (
    <Paper
        variant="outlined"
        sx={{
            borderRadius: '12px',
            p: 1.5,
            borderColor: 'divider',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
        }}
    >
        {/* top row: title + betrag chip */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, lineHeight: 1.3 }}>
                {row.grund || 'Unbenannter Strafgrund'}
            </Typography>
            <BetragChip value={row.betrag} typ={row.typ} />
        </Stack>

        {/* description */}
        {row.individueller_text && (
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.6, lineHeight: 1.4 }}
            >
                {row.individueller_text}
            </Typography>
        )}
    </Paper>
);

// ─── main component ───────────────────────────────────────────────────────────

const StrafenKatalog = ({ catalog = [] }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [search, setSearch] = useState('');
    const [filterTyp, setFilterTyp] = useState('ALL');

    const rows = catalog.map((entry, idx) => ({
        id: entry.id ?? `${entry.grund}-${idx}`,
        grund: entry.grund,
        individueller_text: entry.individueller_text,
        typ: entry.typ,
        betrag: entry.betrag,
    }));

    const filtered = rows.filter((r) => {
        const matchSearch =
            !search ||
            (r.grund || '').toLowerCase().includes(search.toLowerCase()) ||
            (r.individueller_text || '').toLowerCase().includes(search.toLowerCase());
        const matchTyp = filterTyp === 'ALL' || r.typ === filterTyp;
        return matchSearch && matchTyp;
    });

    // sort by betrag descending (mirrors DataGrid initialState)
    const sorted = [...filtered].sort((a, b) => (b.betrag || 0) - (a.betrag || 0));

    return (
        <Box sx={{ mt: 3 }}>
            {/* ── header ── */}
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
                Strafenkatalog
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Standardisierte Strafen für die Teamkasse. Diese Liste hilft dabei, häufige Verstöße
                transparent und schnell zuzuordnen.
            </Typography>

            {catalog.length === 0 ? (
                <Paper
                    sx={{ p: 4, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}
                >
                    <GavelRoundedIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Noch kein Strafenkatalog vorhanden
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Lege zuerst Standard-Strafen im Bereich Strafen an, damit Trainer und
                        Verantwortliche diese schnell zuweisen können.
                    </Typography>
                </Paper>
            ) : (
                <>
                    {/* ── filter bar ── */}
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        sx={{ mb: 2 }}
                    >
                        <TextField
                            size="small"
                            placeholder="Suchen…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRoundedIcon fontSize="small" color="disabled" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ flex: 1 }}
                        />

                        <ToggleButtonGroup
                            value={filterTyp}
                            exclusive
                            onChange={(_, v) => v && setFilterTyp(v)}
                            size="small"
                            sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                        >
                            <ToggleButton value="ALL" sx={{ px: 2, fontSize: '0.72rem', fontWeight: 700 }}>
                                Alle
                            </ToggleButton>
                            <ToggleButton value="GELD" sx={{ px: 2, fontSize: '0.72rem', fontWeight: 700 }}>
                                Geld
                            </ToggleButton>
                            <ToggleButton value="KISTEN" sx={{ px: 2, fontSize: '0.72rem', fontWeight: 700 }}>
                                Kisten
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    {/* ── result count ── */}
                    <Typography variant="caption" color="text.disabled" sx={{ mb: 1.5, display: 'block' }}>
                        {sorted.length} Einträge
                    </Typography>

                    {/* ── mobile: card list ── */}
                    {isMobile ? (
                        <Stack spacing={1.2}>
                            {sorted.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                    Keine Einträge gefunden.
                                </Typography>
                            ) : (
                                sorted.map((row) => <MobileCard key={row.id} row={row} />)
                            )}
                        </Stack>
                    ) : (
                        /* ── desktop: DataGrid ── */
                        <DataGrid
                            rows={sorted}
                            columns={columns}
                            autoHeight
                            disableRowSelectionOnClick
                            hideFooterSelectedRowCount
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 10 } },
                            }}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: '12px',
                                '& .MuiDataGrid-columnHeaders': {
                                    bgcolor: 'rgba(255,255,255,0.04)',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontWeight: 800,
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    color: 'text.secondary',
                                },
                                '& .MuiDataGrid-row:hover': {
                                    bgcolor: 'rgba(255,255,255,0.03)',
                                },
                                '& .MuiDataGrid-cell': {
                                    borderColor: 'rgba(255,255,255,0.05)',
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                },
                            }}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default StrafenKatalog;